const PERFORMANCE_TIMING_EVENT = 'performance_timing'
const STATIC_PERFORMANCE_TIMING_EVENT = 'static_performance_timing'

const SITE_SPEED_SAMPLE_RATE = 100
const GA_COOKIE_NAME = '_ga'
const NON_PRODUCTION_HOSTS = ['localhost', '127.0.0.1', 'dev', 'uat']

type TimingData = {
  page_load_time: number
  page_download_time: number
  dns_time: number
  redirect_response_time: number
  server_response_time: number
  tcp_connect_time: number
  dom_interactive_time: number
  content_load_time: number
}

let navigationStartTime = 0
let staticTimingData: TimingData | null = null
let sampled: boolean | null = null

const isNonProductionHost = () => {
  const host = location.hostname.toLowerCase()
  return NON_PRODUCTION_HOSTS.some((keyword) => host.includes(keyword))
}

const hashId = (value: string) => {
  let b = 1
  if (!value) return b
  for (let c = value.length - 1; c >= 0; c--) {
    const charCode = value.charCodeAt(c)
    b = ((b << 6) & 268435455) + charCode + (charCode << 14)
    const d = b & 266338304
    b = d !== 0 ? b ^ (d >> 21) : b
  }
  return b
}

const getClientId = () =>
  `; ${document.cookie}`.split(`; ${GA_COOKIE_NAME}=`).pop()?.split(';').shift()?.split(/GA1\.[0-9]\./)[1]

const shouldItBeTracked = () => {
  // Cache: cookie and hostname can't change mid-session
  if (sampled !== null) return sampled

  const clientId = getClientId()

  // Outside production there is usually no _ga cookie: /gtag/ has no proxy
  // on the dev server, so GA never initialises and never sets one. Track
  // anyway there, so performance events stay observable.
  if (!clientId) {
    sampled = isNonProductionHost()
    return sampled
  }

  const rate = Math.min(SITE_SPEED_SAMPLE_RATE, 100)
  sampled = hashId(clientId) % 100 < rate
  return sampled
}

const collectTimingData = (): TimingData | null => {
  const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
  // loadEventStart stays 0 until the load event actually fires
  if (!nav || !nav.loadEventStart) return null

  const timingData: TimingData = {
    page_load_time: Math.round(nav.loadEventStart),
    page_download_time: Math.round(nav.responseEnd - nav.responseStart),
    dns_time: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
    redirect_response_time: Math.round(nav.fetchStart),
    server_response_time: Math.round(nav.responseStart - nav.requestStart),
    tcp_connect_time: Math.round(nav.connectEnd - nav.connectStart),
    dom_interactive_time: Math.round(nav.domInteractive),
    content_load_time: Math.round(nav.domContentLoadedEventStart),
  }

  return Object.values(timingData).some((value) => value < 0) ? null : timingData
}

const whenLoaded = (callback: () => void) => {
  if (document.readyState === 'complete') {
    callback()
    return
  }
  // setTimeout 0 guarantees loadEventStart is filled in
  window.addEventListener('load', () => setTimeout(callback, 0), { once: true })
}

export const usePerformanceTiming = () => {
  const onPerformanceCheckStart = () => {
    navigationStartTime = performance.now()
  }

  const pushTiming = (
    event: typeof PERFORMANCE_TIMING_EVENT | typeof STATIC_PERFORMANCE_TIMING_EVENT,
    label: string
  ) => {
    if (!shouldItBeTracked()) return

    staticTimingData ??= collectTimingData()
    if (!staticTimingData) return

    const duration = Math.round(performance.now() - navigationStartTime)

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event,
      timing: {
        ...staticTimingData,
        page_load_time: duration,
      },
    })

    console.info(`[QIC-Analytics] ${label}: ${duration}ms`)
  }

  const onPageReady = () => {
    whenLoaded(() => pushTiming(STATIC_PERFORMANCE_TIMING_EVENT, 'Static performance timing'))
  }

  const onPerformanceCheckEnd = () => {
    pushTiming(PERFORMANCE_TIMING_EVENT, 'Performance timing')
  }

  return {
    onPerformanceCheckStart,
    onPageReady,
    onPerformanceCheckEnd,
  }
}
